import {
    Component,
    EventEmitter,
    forwardRef,
    Output,
    ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { PlantsImageUploadService } from '../../services/upload-plant-image.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-upload-plant-image',
    standalone: true,
    imports: [CommonModule, MatProgressBarModule, MatIconModule, TranslatePipe],
    templateUrl: './upload-plant-image.component.html',
    styleUrls: ['./upload-plant-image.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UploadPlantImageComponent),
            multi: true
        }
    ]
})
export class UploadPlantImageComponent {
    @Output() fileHandleReady = new EventEmitter<string>();
    @Output() imageUploaded = new EventEmitter<{
        url: string;
        fileHandle: string;
    }>();

    uploading = false;
    progress = 0;
    uploadedImageUrl: string | null = null;
    isDragging = false;
    showSuccessIcon = false;
    currentFileHandle: string | null = null;

    constructor(
        private uploadService: PlantsImageUploadService,
        private toastr: ToastrService
    ) {}

    // Drag & Drop
    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = false;
        const file = event.dataTransfer?.files[0];
        if (file) this.handleFile(file);
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) this.handleFile(file);
    }

    // Main Upload
    public async handleFile(file: File): Promise<void> {
        if (!file.type.startsWith('image/')) return;

        this.resetState();
        this.uploading = true;

        try {
            // 1. Begin Upload → string
            const fileHandle = await this.uploadService
                .beginUpload()
                .toPromise();
            if (!fileHandle) throw new Error('Failed to get file handle');

            this.currentFileHandle = fileHandle;
            this.fileHandleReady.emit(fileHandle);

            // 2. Upload Chunks
            await this.uploadChunks(fileHandle, file);

            // 3. End Upload
            const response = await this.uploadService
                .endUpload(fileHandle, file)
                .toPromise();
            const url =
                (response as any)?.blobUrl || (response as any)?.BlobUrl;
            if (!url) throw new Error('No image URL');

            this.uploadedImageUrl = url;
            this.imageUploaded.emit({ url, fileHandle });

            this.showSuccessIcon = true;
            setTimeout(() => (this.showSuccessIcon = false), 1500);
        } catch (error) {
            console.error('Upload failed:', error);
        } finally {
            this.uploading = false;
        }
    }

    private async uploadChunks(fileHandle: string, file: File): Promise<void> {
        const totalChunks = this.uploadService.getTotalChunks(file);
        for (let i = 0; i < totalChunks; i++) {
            const chunk = this.uploadService.getChunk(file, i);
            const data = await this.uploadService.readChunkAsBase64(chunk);
            const startAt = i * this.uploadService.getChunkSize();
            await this.uploadService
                .uploadChunk(fileHandle, data, startAt)
                .toPromise();
            this.progress = Math.round(((i + 1) / totalChunks) * 100);
        }
    }

    private resetState(): void {
        this.progress = 0;
        this.uploadedImageUrl = null;
        this.showSuccessIcon = false;
    }

    deleteImage(event: Event): void {
        event.stopPropagation();
        this.resetState();
        this.toastr.info('Image deleted', 'Deleted');
    }
}
